import React, { useEffect, useState } from "react";
import {
  Modal,
  Box,
  Typography,
  Button,
  TextField,
  Autocomplete,
  CircularProgress,
  Tabs,
  Tab,
  Avatar,
} from "@mui/material";
import { createChat, createGroupChat, fetchUsers } from "../service";

interface CreateChatRoomProps {
  open: boolean;
  setSelectedRoomId: React.Dispatch<React.SetStateAction<string>>;
  setOpenModal: React.Dispatch<React.SetStateAction<boolean>>;
  onClose: () => void;
  setSnackbarMessage: React.Dispatch<React.SetStateAction<string>>;
  setSnackbarSeverity: React.Dispatch<
    React.SetStateAction<"success" | "error">
  >;
  setSnackbarOpen: React.Dispatch<React.SetStateAction<boolean>>;
  fetchChatRooms: () => void;
}

const CreateChatRoom: React.FC<CreateChatRoomProps> = ({
  open,
  setSelectedRoomId,
  setOpenModal,
  onClose,
  setSnackbarMessage,
  setSnackbarSeverity,
  setSnackbarOpen,
  fetchChatRooms,
}) => {
  const [searchTermUser, setSearchTermUser] = useState("");
  const [roomName, setRoomName] = useState("");
  const [userOptions, setUserOptions] = useState<any[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<any[]>([]);
  const [selectedUser, setSelectedUser] = useState<any | null>(null);
  const [selectedUsers, setSelectedUsers] = useState<any[]>([]);
  const [tabIndex, setTabIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      const users = await fetchUsers(page, 10, searchTermUser);

      if (users.length < 10) {
        setHasMore(false);
      }

      setUserOptions((prev) => (page === 1 ? users : [...prev, ...users]));
      setFilteredUsers((prev) => (page === 1 ? users : [...prev, ...users]));

      setLoading(false);
    };

    fetchData();
  }, [page, searchTermUser]);

  useEffect(() => {
    if (!searchTermUser) {
      setFilteredUsers(userOptions);
      return;
    }

    setLoading(true);

    const results = userOptions.filter((user) =>
      [user.display_name, user.user_email]
        .filter(Boolean)
        .some((field) =>
          field.toLowerCase().includes(searchTermUser.toLowerCase())
        )
    );

    setFilteredUsers(
      results.length > 0
        ? results
        : [{ display_name: "Không tồn tại user", user_email: "" }]
    );

    setLoading(false);
  }, [searchTermUser, userOptions]);

  const handleScroll = (event) => {
    const listboxNode = event.currentTarget;
    if (
      listboxNode.scrollTop + listboxNode.clientHeight >=
      listboxNode.scrollHeight - 10
    ) {
      if (hasMore && !loading) {
        setPage((prev) => prev + 1);
      }
    }
  };

  const handleCreateChat = async () => {
    try {
      setLoading(true);
      let response;

      if (tabIndex === 0) {
        if (!selectedUser || !selectedUser._id) return;
        response = await createChat(selectedUser._id, "personal");
      } else {
        if (selectedUsers.length < 2) return;

        const partnerIds = selectedUsers.map((user) => user._id).join(",");

        response = await createGroupChat(partnerIds, roomName, "group");
      }

      if (
        response &&
        response.data &&
        response.data._id &&
        response.data?.chat_room_id?._id
      ) {
        const newChatId = response.data?.chat_room_id?._id;
        console.log("newChatId", newChatId);
        setSelectedRoomId(newChatId);
        setOpenModal(true);
      }

      onClose();
      fetchChatRooms();
      setSnackbarMessage(`Tạo phòng chat thành công!`);
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
    } catch (error) {
      console.log(error);
      setSnackbarMessage(`Tạo phòng chat thất bại!`);
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} aria-labelledby="chat-room-modal">
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: 400,
          bgcolor: "background.paper",
          boxShadow: 24,
          p: 3,
          borderRadius: 2,
        }}
      >
        <Typography variant="h6" gutterBottom fontWeight={550} mb={2}>
          Tạo phòng chat mới
        </Typography>
        <Tabs
          value={tabIndex}
          onChange={(e, newIndex) => setTabIndex(newIndex)}
        >
          <Tab label="Người dùng" />
          <Tab label="Nhiều người dùng" />
        </Tabs>

        {tabIndex === 0 && (
          <Box sx={{ mt: 2, mb: 2 }}>
            <Autocomplete
              value={selectedUser}
              onChange={(event, newValue) => setSelectedUser(newValue)}
              inputValue={searchTermUser}
              onInputChange={(event, newInputValue) => {
                setSearchTermUser(newInputValue);
                setPage(1);
              }}
              options={filteredUsers}
              getOptionLabel={(option) =>
                option.display_name
                  ? `${option.display_name} (${option.user_email})`
                  : ""
              }
              ListboxProps={{
                onScroll: handleScroll,
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Người dùng"
                  variant="outlined"
                  fullWidth
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loading && (
                          <CircularProgress color="inherit" size={20} />
                        )}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          </Box>
        )}

        {tabIndex === 1 && (
          <Box sx={{ mt: 2, mb: 2 }}>
            <TextField
              label="Tên phòng chat"
              variant="outlined"
              fullWidth
              size="small"
              sx={{ mb: 2 }}
              value={roomName}
              onChange={(e) => setRoomName(e.target.value)}
            />
            <Autocomplete
              multiple
              value={selectedUsers}
              onChange={(event, newValue) => setSelectedUsers(newValue)}
              inputValue={searchTermUser}
              onInputChange={(event, newInputValue) => {
                setSearchTermUser(newInputValue);
                setPage(1);
              }}
              options={filteredUsers}
              getOptionLabel={(option) =>
                option.display_name
                  ? `${option.display_name} (${option.user_email})`
                  : ""
              }
              ListboxProps={{
                onScroll: handleScroll,
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Chọn nhiều người dùng"
                  variant="outlined"
                  fullWidth
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loading && (
                          <CircularProgress color="inherit" size={20} />
                        )}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />

            <Box sx={{ display: "flex", alignItems: "center", mt: 2, gap: 1 }}>
              {selectedUsers.slice(0, 4).map((user, index) => (
                <Avatar
                  key={index}
                  src={user.user_avatar}
                  alt={user.display_name}
                />
              ))}

              {selectedUsers.length > 4 && (
                <Avatar sx={{ bgcolor: "gray" }}>
                  +{selectedUsers.length - 4}
                </Avatar>
              )}
            </Box>
          </Box>
        )}

        <Box display="flex" justifyContent="flex-end">
          <Button onClick={onClose} sx={{ mr: 1 }}>
            Hủy
          </Button>
          <Button variant="contained" onClick={handleCreateChat}>
            Tạo
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default CreateChatRoom;
